/**
 * The reviewer's queue: every approval waiting on THIS caller, across the
 * repository.
 *
 * Three things about it are load-bearing.
 *
 * 1. **It is not the transaction approval inbox.** A document approval is
 *    staged in the same table and shows the same levels, but it is filed under
 *    the synthetic object name `DMS_<repo>` and promoting it means moving one
 *    version pointer — which only the repository knows how to do. So it is
 *    decided through `/dms/approvals/decide`, and this queue is where that
 *    happens.
 * 2. **Sending one back requires a reason.** The engine accepts an empty
 *    comment; this does not. It returns the document to its owner, and one with
 *    nothing to act on is one they cannot act on. The control says "Send back
 *    to <name>" rather than "Reject": nothing is being thrown away, it is
 *    going to a named person to fix, which is also why the reason is the whole
 *    payload.
 * 3. **The diff is against the current release, not the previous row.** What a
 *    reviewer needs to know is what changes if they approve — so the comparison
 *    is candidate-vs-the-version-people-are-being-served-today, which for a
 *    document whose last two revisions were both rejected is not the same as
 *    "the version before this one".
 */

import { useCallback, useEffect, useState } from 'react'
import { DmsApprovalReview } from './DmsApprovalReview'
import { humaniseState } from './contracts'
import type {
  DmsApprovalDetail, DmsClient, DmsPendingApproval,
} from './contracts'
import {
  btnDangerStyle, btnPrimaryStyle, btnStyle, explainStyle, inputStyle,
  labelStyle, mutedStyle, RADIUS, T, TYPE,
} from './styles'
import { FactChip } from './ui'

export interface ApprovalQueueProps {
  client: DmsClient
  /** Open the document itself — an approver reads the real file through the
   *  real viewer under the real ACL, never a copy staged in the approval. */
  onOpen: (documentId: string) => void
  /** Refresh the surrounding list after a decision lands. */
  onDecided?: () => void
}

export function ApprovalQueue({ client, onOpen, onDecided }: ApprovalQueueProps) {
  const [pending, setPending] = useState<DmsPendingApproval[]>([])
  const [selected, setSelected] = useState('')
  const [detail, setDetail] = useState<DmsApprovalDetail | null>(null)
  const [reason, setReason] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async () => {
    if (!client.pendingApprovals) return
    try {
      const rows = await client.pendingApprovals()
      setPending(rows)
      setSelected((current) => (
        rows.some((row) => row.instance_id === current)
          ? current
          : String(rows[0]?.instance_id || '')))
    } catch (listError) {
      setPending([])
      setError(listError instanceof Error ? listError.message
        : 'The approval queue could not be listed.')
    }
  }, [client])

  useEffect(() => { void load() }, [load])

  // The selected approval. The queue needs it for its own decision copy ("…is
  // now the current release"); the review body below loads the rest.
  useEffect(() => {
    let live = true
    setDetail(null)
    setReason('')
    setRejecting(false)
    if (!selected || !client.approvalDetail) return undefined

    void (async () => {
      try {
        const loaded = await client.approvalDetail!(selected)
        if (!live) return
        setDetail(loaded)
        setError('')
      } catch (detailError) {
        if (live) {
          setError(detailError instanceof Error ? detailError.message
            : 'This approval could not be opened.')
        }
      }
    })()
    return () => { live = false }
  }, [client, selected])

  const owner = pending.find((row) => row.instance_id === selected)?.submitted_by || ''

  const decide = useCallback(async (verb: 'approve' | 'reject') => {
    if (!client.decideApproval || !detail) return
    if (verb === 'reject' && !reason.trim()) {
      setError(`Say why you are sending this back. It goes to ${owner || 'the person who sent it'}, `
               + 'and there is nothing else for them to work from.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const result = await client.decideApproval({
        instanceId: selected,
        decision: verb,
        comment: reason.trim(),
      })
      const status = String(result.status || '')
      setNotice(
        status === 'approved'
          ? `Approved. "${detail.title}" is now the version in force.`
          : status === 'rejected'
            ? `Sent back to ${owner || 'its owner'} with your reason. `
              + 'The version already in force has not changed.'
            // A cleared level is not a decided chain, and the difference
            // matters to the person who just clicked.
            : `Your approval is recorded. "${detail.title}" is now with the `
              + 'next approver, so nothing has changed for readers yet.')
      setReason('')
      setRejecting(false)
      await load()
      onDecided?.()
    } catch (decideError) {
      setError(decideError instanceof Error ? decideError.message
        : 'The decision could not be recorded.')
    } finally {
      setBusy(false)
    }
  }, [client, detail, load, onDecided, owner, reason, selected])

  if (!client.pendingApprovals) return null

  return (
    /* `flex: 1` for the standalone case (a flex-column parent) and a real
       `minHeight` for the embedded one — inside "Waiting for you" this sits in
       a scrolling block, where `flex: 1; minHeight: 0` would collapse both
       panes to nothing. */
    <div style={{
      flex: 1, minHeight: 420, display: 'flex', overflow: 'hidden',
      border: `1px solid ${T.border}`, borderRadius: RADIUS.lg,
      background: T.surface,
    }}>
      <aside style={listStyle}>
        {pending.length === 0 ? (
          <p style={mutedStyle}>Nothing is waiting for your approval.</p>
        ) : pending.map((row) => {
          const active = row.instance_id === selected
          return (
            <button
              key={row.instance_id}
              type="button"
              onClick={() => { setSelected(row.instance_id); setNotice('') }}
              style={{
                ...cardStyle,
                borderColor: active ? T.accent : T.border,
                background: active ? T.accentWeak : 'transparent',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: TYPE.meta, color: active ? T.accent : T.text }}>
                  {row.title || 'Untitled'}
                </span>
                <FactChip tone="warn">Waiting for you</FactChip>
              </span>
              <span style={{ fontSize: TYPE.small, color: T.textSecondary, lineHeight: 1.5 }}>
                Sent by {row.submitted_by || 'someone who is no longer listed'}
                {row.pending_roles ? ` · now with ${row.pending_roles}` : ''}
              </span>
              {row.target_state ? (
                <span style={{ fontSize: TYPE.small, color: T.textFaint }}>
                  If everyone approves: {humaniseState(row.target_state).toLowerCase()}
                </span>
              ) : null}
            </button>
          )
        })}
      </aside>

      <section style={detailStyle}>
        {notice ? <p style={noticeStyle}>{notice}</p> : null}
        {error ? <p style={errStyle}>{error}</p> : null}

        {!detail ? (
          <p style={mutedStyle}>
            {pending.length === 0
              ? 'When a document is sent for your approval it appears here.'
              : 'Choose one to review it.'}
          </p>
        ) : (
          <>
            <DmsApprovalReview
              client={client}
              instanceId={selected}
              detail={detail}
              onOpen={onOpen}
              onError={setError}
            />
            <p style={{ ...explainStyle, margin: '10px 0 0' }}>
              This shows what changes. Open the document itself to read the
              whole thing.
            </p>

            <footer style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rejecting ? (
                <div>
                  <label style={labelStyle} htmlFor="dms-send-back-reason">
                    Why are you sending it back?
                  </label>
                  <textarea
                    id="dms-send-back-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={3}
                    placeholder={owner
                      ? `${owner} sees this. Say what has to change.`
                      : 'The owner sees this. Say what has to change.'}
                    style={textareaStyle}
                  />
                  <div style={{ ...mutedStyle, marginTop: 6 }}>
                    Required. It is kept with the document as part of its record.
                  </div>
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 8 }}>
                {rejecting ? (
                  <>
                    <button type="button" style={btnDangerStyle} disabled={busy}
                            onClick={() => void decide('reject')}>
                      {busy ? 'Recording…' : `Send back${owner ? ` to ${owner}` : ''}`}
                    </button>
                    <button type="button" style={btnStyle} disabled={busy}
                            onClick={() => { setRejecting(false); setReason(''); setError('') }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" style={btnPrimaryStyle} disabled={busy}
                            onClick={() => void decide('approve')}>
                      {busy ? 'Recording…' : 'Approve'}
                    </button>
                    <button type="button" style={btnStyle} disabled={busy}
                            title="Returns it to the person who sent it, with your reason"
                            onClick={() => { setRejecting(true); setError('') }}>
                      Send back{owner ? ` to ${owner}` : ''}
                    </button>
                  </>
                )}
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  )
}

// ── styles ─────────────────────────────────────────────────────────────────

const listStyle: React.CSSProperties = {
  width: 300,
  flexShrink: 0,
  overflow: 'auto',
  padding: 14,
  borderRight: `1px solid ${T.border}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}

const detailStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'auto',
  padding: 16,
}

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  textAlign: 'left',
  padding: '10px 12px',
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  cursor: 'pointer',
  color: 'inherit',
  fontFamily: 'inherit',
}

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  display: 'block',
  width: '100%',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  boxSizing: 'border-box',
}

const errStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.error,
}

const noticeStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.success,
}
